import { apiClient } from '../lib/apiClient';

/**
 * The feature that produced a memory item. Mirrors the backend's
 * `source_feature` enum (snake_case) defined in src/services/ai/memory.service.ts.
 */
export type MemorySourceFeature = 'reef_insight' | 'journal_analysis' | 'image_insight' | 'ai_output_feedback';

/**
 * A single persisted AI memory item for an aquarium, as returned by
 * GET /api/v1/aquariums/:aquariumId/memory.
 */
export interface AquariumMemoryItem {
  readonly id: string;
  readonly aquariumId: string;
  readonly factText: string;
  readonly sourceFeature: MemorySourceFeature;
  readonly sourceRecordId: string | null;
  readonly confidence: number | null;
  readonly createdAt: string;
}

interface ListMemoryResponseEnvelope {
  data: AquariumMemoryItem[];
}

/**
 * Fetches the non-superseded memory items for an aquarium, most recent first.
 */
export async function fetchAquariumMemory(aquariumId: string): Promise<AquariumMemoryItem[]> {
  const response = await apiClient.get<ListMemoryResponseEnvelope>(
    `/api/v1/aquariums/${aquariumId}/memory`,
  );
  return response.data;
}

/**
 * Deletes a single memory item. Hard delete — backend writes an audit log
 * entry in the same transaction.
 */
export async function deleteMemoryItem(aquariumId: string, memoryId: string): Promise<void> {
  await apiClient.delete(`/api/v1/aquariums/${aquariumId}/memory/${memoryId}`);
}

/**
 * Deletes all memory items for an aquarium. Hard delete — backend writes a
 * single summary audit log entry in the same transaction.
 */
export async function deleteAllMemory(aquariumId: string): Promise<void> {
  await apiClient.delete(`/api/v1/aquariums/${aquariumId}/memory`);
}
